
import React, { useState, useEffect } from 'react';
import { 
    Menu, ChevronDown, CheckCircle2, XCircle, Phone, 
    ShieldCheck, Zap, Clock, TrendingUp, Calendar, 
    ArrowRight, User, Play, ChevronUp, Star, CloudRain,
    HardHat, FileText
} from 'lucide-react';
import ROICalculator from './components/ROICalculator';
import ChatDemo from './components/ChatDemo';
import AudioPlayer from './components/AudioPlayer';
import BookingWidget from './components/BookingWidget';
import { getCalApi } from "@calcom/embed-react";

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Initialize Cal.com Embed
  useEffect(() => {
    (async function () {
      const cal = await getCalApi({"namespace":"15min"});
      cal("ui", {"hideEventTypeDetails":false,"layout":"month_view"});
    })();
  }, []);

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const FAQs = [
      {
          question: "Does this integrate with AccuLynx, Jobber, or ServiceTitan?",
          answer: "Yes. We push the lead details, storm damage notes, and gate codes directly into your CRM. If you use a calendar like Google Calendar for inspections, we sync with that too."
      },
      {
          question: "Can it tell the difference between a repair and a replacement?",
          answer: "Yes. We train the AI to ask qualifying questions like 'How old is the roof?' and 'Is there active leaking?'. If it's a small repair, we can route it differently than a full reroof opportunity."
      },
      {
          question: "What if the AI doesn't know the answer?",
          answer: "It politely takes a message and texts you the details instantly so you can follow up personally."
      },
      {
          question: "Will it sound like a robot?",
          answer: "No. We use V6 low-latency audio models that breathe, pause, and intonate just like a human. 92% of homeowners believe they are speaking to a local office manager."
      },
      {
          question: "Is there a long-term contract?",
          answer: "No. We know roofing is seasonal. We operate on a month-to-month basis so you can scale up during hail season and scale down in the winter."
      }
  ];

  const Logo = () => (
    <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* The Signal Shelter: Minimalist Geometric Mark */}
        {/* The Roof (Protection/Structure) */}
        <path d="M6 22L20 10L34 22" stroke="white" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
        {/* The Signal (AI Core/Voice) */}
        <rect x="17" y="24" width="6" height="10" rx="3" fill="white"/>
    </svg>
  );

  return (
    <div className="min-h-screen bg-black text-zinc-100 selection:bg-orange-500/30 selection:text-orange-200 font-sans">
      
      {/* 1. NAVIGATION - Sticky, Logo Left, CTA Right, Minimal Links */}
      <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? 'py-2' : 'py-6'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`glass-nav rounded-full px-6 flex justify-between items-center transition-all duration-300 ${scrolled ? 'h-14 bg-black/90 border-zinc-800' : 'h-16 bg-transparent border-transparent'}`}>
                {/* Logo Left */}
                <button 
                    onClick={scrollToTop}
                    className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                >
                     <Logo />
                    <span className="font-bold text-lg tracking-tight text-white hidden sm:block">Hire Nobody</span>
                </button>
                
                {/* Links Center - Minimal */}
                <div className="hidden md:flex items-center gap-8">
                    <a href="#how-it-works" className="text-sm font-medium text-zinc-400 hover:text-white transition-colors">How it Works</a>
                    <a href="#results" className="text-sm font-medium text-zinc-400 hover:text-white transition-colors">Results</a>
                    <a href="#faq" className="text-sm font-medium text-zinc-400 hover:text-white transition-colors">FAQ</a>
                </div>

                {/* CTA Right */}
                <div className="hidden md:block">
                     <button 
                        data-cal-namespace="15min"
                        data-cal-link="talescouto/15min"
                        data-cal-config='{"layout":"month_view"}'
                        className="bg-white text-black px-5 py-2.5 rounded-full text-xs font-bold hover:bg-zinc-200 transition-transform active:scale-95 border border-white"
                    >
                        Book My Strategy Call
                    </button>
                </div>

                <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-white">
                    <Menu size={24} />
                </button>
            </div>
        </div>
      </nav>

      {/* 2. HERO SECTION - High Stakes, Industry Specific */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-48 pb-20">
        {/* Spotlight */}
        <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-orange-500/5 rounded-full blur-[120px] pointer-events-none"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            
            {/* Headline: Visual Hierarchy Improvement */}
            <div className="animate-fade-in-up">
                <h1 className="text-6xl sm:text-7xl lg:text-9xl font-extrabold tracking-tighter mb-10 leading-[1]">
                    <span className="block text-white mb-2">Turn Missed Calls</span>
                    <span className="block text-gradient-orange pb-3">Into $25k Contracts.</span>
                </h1>
            </div>

            {/* Subhead: The Specific Solution */}
            <p className="text-xl md:text-2xl text-zinc-400 max-w-2xl mx-auto mb-12 leading-relaxed font-light animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                Our 24/7 AI Receptionist answers every homeowner instantly, qualifies leads, and books inspections directly to your calendar. You never miss a job while you’re on a roof.
            </p>

            {/* CTA: First Person Action */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                <button
                    data-cal-namespace="15min"
                    data-cal-link="talescouto/15min"
                    data-cal-config='{"layout":"month_view"}'
                    className="min-w-[220px] px-8 py-5 bg-[#FF5500] hover:bg-[#ff661a] text-white rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 group shadow-[0_0_30px_rgba(255,85,0,0.2)] hover:shadow-[0_0_40px_rgba(255,85,0,0.4)] transform hover:-translate-y-1"
                >
                    Get My Free Demo
                </button>
                <a 
                    href="#demo"
                    className="min-w-[220px] px-8 py-5 bg-transparent text-white border border-zinc-700 rounded-xl font-bold text-lg hover:bg-zinc-900 hover:border-zinc-500 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-3"
                >
                   <Play size={20} fill="currentColor" /> Hear Live Demo
                </a>
            </div>
            
            {/* Visual: Happy "After" State (Calendar) - Flattened */}
            <div className="mt-24 relative w-full max-w-5xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                <div className="relative rounded-xl bg-zinc-900 border border-zinc-800 p-2 shadow-2xl group">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent via-transparent to-black/80 z-20 pointer-events-none group-hover:opacity-50 transition-opacity"></div>
                    <div className="bg-black rounded-lg overflow-hidden grid grid-cols-7 gap-px border border-zinc-800 opacity-90">
                         {/* Mock Calendar Header */}
                         <div className="col-span-7 bg-zinc-900 p-4 border-b border-zinc-800 flex justify-between items-center">
                            <span className="font-bold text-white text-sm tracking-wide">OCTOBER 2024</span>
                            <div className="flex gap-2">
                                <span className="px-3 py-1 bg-green-500/10 text-green-400 text-[10px] font-bold uppercase tracking-wider rounded-full border border-green-500/20 shadow-[0_0_10px_rgba(74,222,128,0.1)]">+14 Inspections</span>
                            </div>
                         </div>
                         {/* Mock Calendar Grid */}
                         {Array.from({length: 14}).map((_, i) => (
                             <div key={i} className="h-32 bg-zinc-950/50 p-2 border border-zinc-900/50 hover:bg-zinc-900/80 transition-colors">
                                 <div className="text-zinc-600 text-[10px] mb-2 font-medium">{i+1}</div>
                                 {i % 2 === 0 && (
                                     <div className="bg-orange-500/10 border-l-[3px] border-orange-500 p-2 rounded-r-md mb-2 shadow-sm">
                                         <div className="text-[10px] text-orange-200 font-bold mb-0.5">Inspect: 123 Oak</div>
                                         <div className="text-[9px] text-orange-400/70">Leak • 35sq</div>
                                     </div>
                                 )}
                                 {i % 3 === 0 && (
                                     <div className="bg-blue-500/10 border-l-[3px] border-blue-500 p-2 rounded-r-md shadow-sm">
                                         <div className="text-[10px] text-blue-200 font-bold">New Roof Est</div>
                                     </div>
                                 )}
                             </div>
                         ))}
                    </div>
                    {/* Floating Badge */}
                    <div className="absolute bottom-8 right-8 z-30 bg-zinc-900/90 backdrop-blur-xl border border-zinc-700 p-6 rounded-2xl shadow-2xl flex items-center gap-5 translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                        <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center shadow-[0_0_20px_rgba(34,197,94,0.4)]">
                            <TrendingUp className="text-black" size={24} />
                        </div>
                        <div>
                            <div className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold mb-0.5">Projected Revenue</div>
                            <div className="text-2xl font-bold text-white tracking-tight">$42,500</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Social Proof - Industry specific - Moved Below Visual */}
            <div className="mt-16 flex flex-col items-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
                <div className="flex items-center gap-4 text-zinc-600 text-[10px] font-bold uppercase tracking-[0.2em]">
                    <span>Synced With</span>
                    <div className="h-px w-8 bg-zinc-800"></div>
                    <div className="flex gap-6 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
                        <span className="text-white hover:text-orange-500 transition-colors">ACCULYNX</span>
                        <span className="text-white hover:text-orange-500 transition-colors">JOBBER</span>
                        <span className="text-white hover:text-orange-500 transition-colors">SERVICETITAN</span>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* 3. PROBLEM SECTION - Agitation, Bullets, Empathy */}
      <section className="py-24 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-20 text-center">
                <h2 className="text-3xl md:text-5xl font-bold tracking-tighter text-white mb-6">
                    Is Your Busy Schedule Costing You Jobs?
                </h2>
                <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
                    You spend thousands on Angi leads, yard signs, and PPC. But your sales process has holes that are draining your profit.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                    { icon: Clock, title: "The Speed-to-Lead Problem", desc: "If you don't answer a lead in 2 minutes, they call the next roofer. Speed is the only thing that matters." },
                    { icon: XCircle, title: "The 'Free Estimate' Trap", desc: "You waste gas and hours driving 45 mins just to find a roof with zero damage and no claim." },
                    { icon: Phone, title: "The Unanswered Ring", desc: "When marketing blasts go out, or Monday morning hits, your phone rings off the hook. You can't answer them all." },
                    { icon: User, title: "The Shared Lead War", desc: "Buying shared leads? You are fighting 5 other roofers. If you aren't first, you're last." }
                ].map((item, i) => (
                    <div key={i} className="bg-black border border-zinc-800 rounded-3xl p-8 hover:border-zinc-600 transition-all group duration-300">
                        <div className="w-14 h-14 rounded-2xl bg-zinc-900 flex items-center justify-center mb-6 border border-zinc-800 group-hover:border-red-500/50 transition-colors">
                            <item.icon className="text-red-500 group-hover:scale-110 transition-transform duration-300" size={28} />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                        <p className="text-zinc-500 leading-relaxed text-sm">
                            {item.desc}
                        </p>
                    </div>
                ))}
            </div>
            
            <div className="mt-16 text-center">
                <p className="text-zinc-500 italic font-medium">"We get it. You can't be tearing off a roof and answering the phone at the same time."</p>
            </div>
        </div>
      </section>

      {/* 4. SOLUTION SECTION - The Guide & Human Connection */}
      <section className="py-24 bg-black border-t border-zinc-900" id="demo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
                
                {/* Text Side */}
                <div className="order-2 lg:order-1">
                    <div className="inline-block px-4 py-1.5 rounded-full bg-zinc-900 border border-zinc-800 text-[10px] font-bold uppercase tracking-widest text-orange-500 mb-8 shadow-sm">
                        Meet Your New Top Producer
                    </div>
                    <h2 className="text-4xl md:text-6xl font-bold text-white mb-8 tracking-tighter leading-[1.1]">
                        Built by Roofers, <br />
                        <span className="text-zinc-600">For Roofers.</span>
                    </h2>
                    
                    {/* The "Guide" Human Element */}
                    <div className="flex items-start gap-4 mb-10 bg-gradient-to-br from-zinc-900 to-black p-6 rounded-2xl border border-zinc-800">
                         <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Mike&backgroundColor=b6e3f4" alt="Founder" className="w-14 h-14 rounded-full border-2 border-zinc-700 shadow-lg" />
                         <div>
                             <p className="text-zinc-300 text-sm leading-relaxed italic mb-3">
                                 "We know the struggle. You can't be tearing off a roof and closing a deal at the same time. But hiring a human office manager is expensive. That’s why we built the perfect employee."
                             </p>
                             <div className="text-white font-bold text-xs tracking-wide uppercase flex items-center gap-2">
                                <div className="w-4 h-[1px] bg-orange-500"></div> Mike, Lead Engineer
                             </div>
                         </div>
                    </div>

                    <p className="text-lg text-zinc-400 mb-10 leading-relaxed font-light">
                        Hire Nobody V6 isn't just a chatbot. It's a fully trained roofing expert. It knows the difference between 3-tab and architectural shingles. It knows how to ask for the gate code. It books the job.
                    </p>

                    <div className="flex gap-12">
                        <div className="flex flex-col">
                            <span className="text-4xl font-bold text-white tracking-tight">1.2M+</span>
                            <span className="text-[10px] text-zinc-600 uppercase tracking-widest font-bold mt-1">Calls Answered</span>
                        </div>
                        <div className="w-px h-14 bg-zinc-800"></div>
                        <div className="flex flex-col">
                            <span className="text-4xl font-bold text-white tracking-tight">40M+</span>
                            <span className="text-[10px] text-zinc-600 uppercase tracking-widest font-bold mt-1">Booked Revenue</span>
                        </div>
                    </div>
                </div>

                {/* Visual Side - Voice Demo */}
                <div className="order-1 lg:order-2 flex justify-center lg:justify-end relative group">
                    <div className="absolute inset-0 bg-gradient-to-tr from-orange-500/20 to-blue-500/10 blur-[100px] rounded-full opacity-50 group-hover:opacity-70 transition-opacity duration-700 pointer-events-none"></div>
                    
                    {/* Phone Container */}
                    <div className="transform group-hover:scale-[1.02] transition-transform duration-500 flex flex-col items-center">
                        <ChatDemo />
                        
                        {/* Disclaimer */}
                        <div className="mt-6 flex items-center gap-2 px-4 py-2 bg-zinc-900/80 backdrop-blur-md border border-zinc-800/50 rounded-full">
                            <div className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse"></div>
                            <p className="text-[10px] text-zinc-400 font-medium">
                                Web demo has slight delay. Real phone calls are instant.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* 5. BENEFIT SECTION - Outcome Oriented */}
      <section className="py-24 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-20 text-center tracking-tight">How We Help You Dominate Your Market</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
                {/* Benefit 1 */}
                <div className="p-8 rounded-3xl bg-black border border-zinc-800 hover:border-zinc-700 transition-colors duration-300">
                    <div className="w-14 h-14 bg-zinc-900 rounded-2xl flex items-center justify-center mb-8 text-white border border-zinc-800 shadow-inner">
                        <Zap size={24} className="text-orange-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">Never Miss A Lead Again</h3>
                    <div className="mb-6">
                         <div className="inline-block px-3 py-1 rounded-md bg-zinc-900 border border-zinc-800 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                            Outcome: First Responder
                         </div>
                    </div>
                    <p className="text-zinc-400 text-sm leading-7">
                        Our AI scales instantly to handle multiple calls at once. You capture every lead while competitors get busy signals.
                    </p>
                </div>

                {/* Benefit 2 */}
                <div className="p-8 rounded-3xl bg-black border border-zinc-800 hover:border-zinc-700 transition-colors duration-300">
                    <div className="w-14 h-14 bg-zinc-900 rounded-2xl flex items-center justify-center mb-8 text-white border border-zinc-800 shadow-inner">
                        <Calendar size={24} className="text-orange-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">Only Visit Ready-to-Buy Homeowners</h3>
                    <div className="mb-6">
                         <div className="inline-block px-3 py-1 rounded-md bg-zinc-900 border border-zinc-800 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                            Outcome: Zero Tire Kickers
                         </div>
                    </div>
                    <p className="text-zinc-400 text-sm leading-7">
                        The AI filters out tire-kickers so you only drive to qualified inspections.
                    </p>
                </div>

                {/* Benefit 3 */}
                <div className="p-8 rounded-3xl bg-black border border-zinc-800 hover:border-zinc-700 transition-colors duration-300">
                    <div className="w-14 h-14 bg-zinc-900 rounded-2xl flex items-center justify-center mb-8 text-white border border-zinc-800 shadow-inner">
                        <ShieldCheck size={24} className="text-orange-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">Keep 100% of Your Profit</h3>
                    <div className="mb-6">
                         <div className="inline-block px-3 py-1 rounded-md bg-zinc-900 border border-zinc-800 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                            Outcome: 100% Margin
                         </div>
                    </div>
                    <p className="text-zinc-400 text-sm leading-7">
                        Stop paying commissions. Our AI works 24/7 for a flat rate.
                    </p>
                </div>
            </div>
        </div>
      </section>

      {/* 6. ACTION PLAN - 1, 2, 3 Steps */}
      <section id="how-it-works" className="py-24 bg-black border-y border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-24">
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight">From "Missed Call" to "Signed Contract"</h2>
                <p className="text-zinc-500">No complex onboarding. We handle the tech.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-12 relative">
                {/* Connector Line (Desktop) */}
                <div className="hidden md:block absolute top-12 left-0 w-full h-px bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 z-0"></div>

                {/* Step 1 */}
                <div className="relative z-10 text-center group">
                    <div className="w-24 h-24 mx-auto bg-black border-4 border-zinc-900 rounded-full flex items-center justify-center text-3xl font-bold text-white mb-8 shadow-2xl group-hover:border-zinc-700 transition-colors duration-300">
                        1
                    </div>
                    <h3 className="text-xl font-bold text-white mb-4">We Build Your AI Receptionist</h3>
                    <p className="text-zinc-400 text-sm px-6 leading-relaxed">
                        We customize your script and knowledge base in just 48 hours. No tech skills required.
                    </p>
                </div>

                {/* Step 2 */}
                <div className="relative z-10 text-center group">
                    <div className="w-24 h-24 mx-auto bg-black border-4 border-orange-500 rounded-full flex items-center justify-center text-3xl font-bold text-white mb-8 shadow-[0_0_40px_rgba(249,115,22,0.2)] scale-110">
                        2
                    </div>
                    <h3 className="text-xl font-bold text-white mb-4">You Update Your Number</h3>
                    <p className="text-zinc-400 text-sm px-6 leading-relaxed">
                        We give you a dedicated AI number. Simply replace your old number on Google and your ads, and you're live.
                    </p>
                </div>

                {/* Step 3 */}
                <div className="relative z-10 text-center group">
                    <div className="w-24 h-24 mx-auto bg-black border-4 border-zinc-900 rounded-full flex items-center justify-center text-3xl font-bold text-white mb-8 shadow-2xl group-hover:border-zinc-700 transition-colors duration-300">
                        3
                    </div>
                    <h3 className="text-xl font-bold text-white mb-4">You Collect The Check</h3>
                    <p className="text-zinc-400 text-sm px-6 leading-relaxed">
                        Your calendar fills up with qualified inspections. You just show up and close the deal.
                    </p>
                </div>
            </div>
        </div>
      </section>

      {/* 7. TESTIMONIALS - Before/After Format */}
      <section id="results" className="py-24 bg-zinc-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-white mb-20 tracking-tight">See What Other Roofers Are Saying</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
                {/* Testimonial 1 */}
                <div className="bg-zinc-900/50 rounded-3xl p-10 border border-zinc-800">
                    <div className="flex gap-1 mb-8">
                        {[1,2,3,4,5].map(s => <Star key={s} size={14} className="fill-orange-500 text-orange-500" />)}
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-8 leading-tight">"Paid for itself in one phone call."</h3>
                    <div className="space-y-4 mb-10 text-sm">
                        <div className="p-5 bg-black rounded-xl border-l-2 border-red-500/50">
                            <span className="text-red-500 uppercase text-[10px] font-bold tracking-widest block mb-2">Before</span>
                            <p className="text-zinc-400 leading-relaxed">"I was missing 5-10 calls a week during hail season because I was on the roof. I knew I was losing money."</p>
                        </div>
                        <div className="p-5 bg-zinc-800/30 rounded-xl border-l-2 border-green-500/50">
                            <span className="text-green-500 uppercase text-[10px] font-bold tracking-widest block mb-2">After</span>
                            <p className="text-zinc-200 leading-relaxed">"Hire Nobody caught a call at 7 PM on a Sunday. Booked a $22k full replacement. ROI is insane."</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4 pt-6 border-t border-zinc-800">
                        <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=MikeR" className="w-10 h-10 rounded-full bg-zinc-800" alt="Mike" />
                        <div>
                            <div className="text-white font-bold text-sm">Mike R.</div>
                            <div className="text-xs text-zinc-500">Owner, Apex Roofing Systems</div>
                        </div>
                        <div className="ml-auto opacity-40 hover:opacity-100 transition-opacity">
                             <span className="font-bold text-zinc-400 italic text-xs">Google Reviews</span>
                        </div>
                    </div>
                </div>

                 {/* Testimonial 2 */}
                 <div className="bg-zinc-900/50 rounded-3xl p-10 border border-zinc-800">
                    <div className="flex gap-1 mb-8">
                        {[1,2,3,4,5].map(s => <Star key={s} size={14} className="fill-orange-500 text-orange-500" />)}
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-8 leading-tight">"Stopped driving to $200 repairs."</h3>
                    <div className="space-y-4 mb-10 text-sm">
                        <div className="p-5 bg-black rounded-xl border-l-2 border-red-500/50">
                            <span className="text-red-500 uppercase text-[10px] font-bold tracking-widest block mb-2">Before</span>
                            <p className="text-zinc-400 leading-relaxed">"My sales guys were driving 40 mins for 'leaks' that turned out to be nothing. Burnt out and angry."</p>
                        </div>
                        <div className="p-5 bg-zinc-800/30 rounded-xl border-l-2 border-green-500/50">
                            <span className="text-green-500 uppercase text-[10px] font-bold tracking-widest block mb-2">After</span>
                            <p className="text-zinc-200 leading-relaxed">"The AI qualifies them hard. If it books an appointment, we know it's a claim. My guys are happier."</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4 pt-6 border-t border-zinc-800">
                        <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=SarahT" className="w-10 h-10 rounded-full bg-zinc-800" alt="Sarah" />
                        <div>
                            <div className="text-white font-bold text-sm">Sarah T.</div>
                            <div className="text-xs text-zinc-500">Ops Manager, Elite Roofs</div>
                        </div>
                        <div className="ml-auto opacity-40 hover:opacity-100 transition-opacity">
                             <span className="font-bold text-zinc-400 italic text-xs">TrustPilot</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* 8. FAQ - Objection Busting */}
      <section id="faq" className="py-24 bg-black">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-12 text-center tracking-tight">Frequently Asked Questions</h2>
            <div className="space-y-4">
                {FAQs.map((faq, index) => (
                    <div key={index} className="border border-zinc-800 rounded-2xl bg-zinc-900/30 overflow-hidden hover:bg-zinc-900/60 transition-colors">
                        <button 
                            onClick={() => toggleFaq(index)}
                            className="w-full flex justify-between items-center p-6 text-left"
                        >
                            <span className="font-semibold text-zinc-200 pr-8">{faq.question}</span>
                            {activeFaq === index ? <ChevronUp className="text-orange-500 flex-shrink-0" size={18} /> : <ChevronDown className="text-zinc-500 flex-shrink-0" size={18} />}
                        </button>
                        {activeFaq === index && (
                            <div className="px-6 pb-6 pt-0 text-zinc-400 text-sm leading-7 animate-fade-in">
                                {faq.answer}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
      </section>

      {/* 9. FINAL CTA - Embedded Booking Widget */}
      <section id="book" className="py-32 bg-zinc-950 relative overflow-hidden">
        {/* Background Glow */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-orange-500/10 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tighter">Ready To Stop Losing Jobs?</h2>
            <p className="text-xl text-zinc-400 mb-12 max-w-2xl mx-auto">
                We accept only 5 new partners per month. Check availability below.
            </p>

            <BookingWidget />
        </div>
      </section>

      <footer className="bg-black py-12 border-t border-zinc-900">
          <div className="max-w-7xl mx-auto px-4 text-center">
              <button 
                  onClick={scrollToTop}
                  className="flex justify-center items-center gap-3 mb-8 mx-auto hover:opacity-80 transition-opacity"
              >
                    <Logo />
                    <span className="font-bold text-zinc-500 tracking-tight">Hire Nobody</span>
              </button>
              <p className="text-zinc-700 text-xs text-center">  © 2024 Hire Nobody. All rights reserved. <br />  <a href="/privacy.html" className="underline" target="_blank" rel="noopener noreferrer">
    Privacy Policy  </a>  <span className="mx-1">|</span>  <a href="/terms.html" className="underline" target="_blank" rel="noopener noreferrer">
    Terms of Service
  </a>
</p>
          </div>
      </footer>

      <ROICalculator isOpen={isCalculatorOpen} onClose={() => setIsCalculatorOpen(false)} />
    </div>
  );
};

export default App;
